/**
 * SessionPausedOverlay — Phase 2
 *
 * Listens for 'session_paused' / 'session_resumed' session_events, and on
 * opening reads the latest of them, so someone who joins during a pause sees it.
 * When paused: renders a full-screen dark overlay with a waiting message.
 * Participants cannot interact with the tool until the facilitator resumes.
 */

import { useEffect, useState } from 'react';
import { getSafeSupabaseClient } from '../../../lib/supabaseSync';
import { fetchLatestSessionEvent } from '../../../lib/sessionEvents';
import { C } from '../theme/participantTokens';

interface SessionPausedOverlayProps {
  sessionId: string;
  /**
   * Set when the screen cannot read the room's events itself (a guest by link,
   * S-8): the pause as the guest's room read reports it. Nothing is subscribed.
   */
  paused?: boolean;
}

export function SessionPausedOverlay({ sessionId, paused }: SessionPausedOverlayProps) {
  const [livePaused, setIsPaused] = useState(false);
  const controlled = paused !== undefined;
  const isPaused = controlled ? paused : livePaused;

  useEffect(() => {
    if (controlled) return;
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    let cancelled = false;

    // Catch up: a pause already in force when this screen opens.
    void fetchLatestSessionEvent(sessionId, ['session_paused', 'session_resumed']).then(latest => {
      if (!cancelled && latest) setIsPaused(latest.event_type === 'session_paused');
    });

    const ch = supabase
      .channel(`pause:${sessionId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'session_events', filter: `session_id=eq.${sessionId}` },
        payload => {
          const ev = payload.new as { event_type: string };
          if (ev.event_type === 'session_paused') setIsPaused(true);
          if (ev.event_type === 'session_resumed') setIsPaused(false);
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(ch);
    };
  }, [sessionId, controlled]);

  if (!isPaused) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(18,22,28,0.88)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 20,
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          border: `2px solid ${C.mustard}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.6rem',
        }}
      >
        ⏸
      </div>
      <div
        style={{
          fontFamily: 'Spectral, Georgia, serif',
          fontSize: '1.3rem',
          fontWeight: 600,
          color: C.textPri,
        }}
      >
        Session paused
      </div>
      <div
        style={{
          fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
          fontSize: '0.85rem',
          fontWeight: 300,
          color: C.textTer,
          textAlign: 'center',
          maxWidth: 320,
          lineHeight: 1.6,
        }}
      >
        The facilitator has paused the session. Hang tight — you'll be resumed shortly.
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: '0.58rem',
          color: C.mustard,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
        }}
      >
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: C.mustard, animation: 'pulse 1.5s infinite' }} />
        Waiting for facilitator
      </div>
    </div>
  );
}
